
import React from "react";
import { createBrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "@/shared/ui/ErrorBoundary";
import { ErrorState } from "@/shared/ui/states/ErrorState";
import { LoadingState } from "@/shared/ui/states/LoadingState";
import DashboardGlassLoader from "@/features/dashboard/components/DashboardGlassLoader";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AuthWrapper } from "@/app/AuthWrapper";
import { Layout } from "@/shared/ui/Layout";
import { PERMISSIONS } from "@/shared/constants/permissions";
import { usePageTitle } from "@/shared/hooks/usePageTitle";
import { adminRoutes } from "./routes/adminRoutes";
import { operationsRoutes } from "./routes/operationsRoutes";
import { systemRoutes } from "./routes/systemRoutes";
import { toolsRoutes } from "./routes/toolsRoutes";

// Lazy load top-level pages
const Dashboard = React.lazy(() => import("@/features/dashboard/components/Dashboard"));
const FleetDashboardPage = React.lazy(() => import("@/features/dashboard/components/FleetDashboardPage"));
const Fleet = React.lazy(() => import("@/features/fleet/components/Fleet"));
const Login = React.lazy(() => import("@/shared/components/Login"));
const ForgotPassword = React.lazy(() => import("@/shared/components/ForgotPassword"));
const LostPassword = React.lazy(() => import("@/shared/components/LostPassword"));
const SSOCallback = React.lazy(() => import('@/shared/components/SSOCallback'));
const RootRedirect = React.lazy(() => import('@/shared/components/RootRedirect'));
const NoAccessPage = React.lazy(() => import("@/pages/NoAccessPage"));
const DashboardNoAccessPage = React.lazy(() => import("@/pages/DashboardNoAccessPage"));
const NotFoundPage = React.lazy(() => import("@/pages/NotFoundPage"));

// Soft-dim fallback shared by the AppLayout-level Suspense + the
// dashboard route's inner Suspense. Inline so it's in the main bundle
// (zero load latency) and visually identical to the in-dashboard
// initial-load overlay — one continuous loading state across the whole
// nav lifecycle. No `backdrop-blur` (the blur shimmers during the fade
// transition, which reads as eye-straining on a text-dense layout).
const GlassRouteFallback: React.FC = () => (
  <div
    className="absolute inset-0 z-10 pointer-events-none bg-surface/60"
    aria-label="Loading"
    role="status"
    data-testid="route-glass-loader"
  />
);

// Layout wrapper component with authentication and shared header
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  usePageTitle();
  return (
    <ErrorBoundary fallback={ErrorState}>
      <AuthWrapper>
        <Layout>
          <React.Suspense fallback={<GlassRouteFallback />}>
            {children}
          </React.Suspense>
        </Layout>
      </AuthWrapper>
    </ErrorBoundary>
  );
};

export const router = createBrowserRouter([
  // --- Auth / standalone pages (no layout) ---
  {
    path: "/login",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <Login />
      </React.Suspense>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <ForgotPassword />
      </React.Suspense>
    ),
  },
  {
    path: "/lost-password/",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <LostPassword />
      </React.Suspense>
    ),
  },
  {
    path: "/callback",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <SSOCallback />
      </React.Suspense>
    ),
  },
  {
    path: "/",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <RootRedirect />
      </React.Suspense>
    ),
  },

  // --- Top-level app pages ---
  {
    path: "/no-access",
    element: (
      <AppLayout>
        <NoAccessPage />
      </AppLayout>
    ),
  },
  {
    path: "/dashboard",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.DASHBOARD_VIEW}
          fallback={
            <React.Suspense fallback={null}>
              <DashboardNoAccessPage />
            </React.Suspense>
          }
        >
          <Dashboard />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/dashboard/fleet",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.DASHBOARD_VIEW}
          fallback={
            <React.Suspense fallback={null}>
              <DashboardNoAccessPage />
            </React.Suspense>
          }
        >
          {/* Inner Suspense overrides AppLayout's generic "Loading page…"
              text fallback with a frosted-glass placeholder while the
              dashboard chunk downloads. Matches the in-page glass
              overlay so the user perceives one continuous loading state
              from chunk download → component mount → data fetch. */}
          <React.Suspense fallback={<DashboardGlassLoader />}>
            <FleetDashboardPage />
          </React.Suspense>
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/fleet",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.VEHICLE_VIEW}>
          <Fleet />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/riders",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Riders</h1>
            <p className="text-text-muted">Coming Soon</p>
          </div>
        </ProtectedRoute>
      </AppLayout>
    ),
  },

  // --- Feature route groups ---
  ...adminRoutes(AppLayout),
  ...operationsRoutes(AppLayout),
  ...systemRoutes(AppLayout),
  ...toolsRoutes(AppLayout),

  // --- Catch-all ---
  {
    path: "*",
    element: (
      <React.Suspense fallback={<LoadingState message="Loading..." />}>
        <NotFoundPage />
      </React.Suspense>
    ),
  },
]);
